/**
 * useUserActivity Hook
 *
 * Meldet den eingeloggten Nutzer als aktiv - beim App-Start und bei jedem
 * Wechsel zurueck in den Vordergrund. Die Datenbank (Migration 167) macht
 * daraus zweierlei: eine Zeile je Nutzer und Tag in user_activity_days
 * ("aktive Nutzer pro Tag" im Admin-Dashboard) und die Felder
 * profiles.last_seen_at / profiles.platform.
 *
 * Grundregel: das hier darf niemals etwas blockieren. Der Aufruf ist reine
 * Telemetrie - schlaegt er fehl, ist das fuer den Nutzer folgenlos und wird
 * nur geloggt. Kein Zustand, kein Rendern, kein await im Startpfad.
 *
 * Drosselung an zwei Stellen, mit verschiedenen Aufgaben:
 *   - hier, 60 Sekunden: verhindert einen Netzaufruf bei jedem kurzen
 *     Wegtippen und Zurueckwechseln.
 *   - in touch_user_activity(), 15 Minuten: verhindert den Schreibvorgang.
 * Die Tageszeile schreibt die Datenbank ungedrosselt, damit der erste Besuch
 * nach Mitternacht nicht verloren geht - deshalb ist die Sperre hier bewusst
 * kurz und nicht ebenfalls 15 Minuten lang.
 */

import { useEffect } from 'react';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth';

/** Kuerzester Abstand zwischen zwei Aufrufen (ms). */
const MIN_INTERVAL_MS = 60 * 1000;

/**
 * Modulweit, nicht pro Komponente: der Hook haengt an der Sitzung, nicht an
 * einem Mount. Bei einem Nutzerwechsel wird die Sperre zurueckgesetzt, sonst
 * verschluckte sie den ersten Aufruf des neuen Nutzers.
 */
let lastTouchAt = 0;
let lastTouchedUserId: string | null = null;

export function useUserActivity(): void {
  const { user } = useAuth();
  const userId = user?.id;

  useEffect(() => {
    if (!userId) return;

    if (userId !== lastTouchedUserId) {
      lastTouchedUserId = userId;
      lastTouchAt = 0;
    }

    let cancelled = false;

    const touch = () => {
      const now = Date.now();
      if (now - lastTouchAt < MIN_INTERVAL_MS) return;
      lastTouchAt = now;

      void (async () => {
        try {
          // getPlatform() liefert genau 'ios' | 'android' | 'web' - dieselben
          // drei Werte, die die CHECK-Constraint zulaesst.
          const { error } = await supabase.rpc('touch_user_activity', {
            p_platform: Capacitor.getPlatform(),
          });
          if (error) {
            console.warn('[useUserActivity] touch_user_activity failed:', error.message);
          }
        } catch (err) {
          // Offline, Proxy, Plugin-Fehler - alles ohne Folgen fuer den Nutzer.
          console.warn('[useUserActivity] touch_user_activity threw:', err);
        }
      })();
    };

    // App-Start (bzw. der Moment, in dem die Session steht).
    touch();

    // Wechsel in den Vordergrund. Der App-Plugin bedient appStateChange auch
    // im Web (ueber visibilitychange), der Listener gilt deshalb fuer alle
    // drei Plattformen. Faellt er aus, bleibt der Start-Aufruf oben bestehen.
    let handle: PluginListenerHandle | undefined;
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) touch();
    })
      .then((h) => {
        // Beim Abmelden kann der Effect schon aufgeraeumt sein, bevor das
        // Handle da ist - dann sofort wieder abmelden.
        if (cancelled) {
          void h.remove();
          return;
        }
        handle = h;
      })
      .catch((err) => {
        console.warn('[useUserActivity] appStateChange listener failed:', err);
      });

    return () => {
      cancelled = true;
      void handle?.remove();
    };
  }, [userId]);
}

export default useUserActivity;
